import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const EMPTY_FORM = { category: "", name: "", discount: "", link: "", notes: "" };

export default function Admin({ user, onLogout }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", is_admin: false });

  const fetchAll = async () => {
    const [d, u] = await Promise.all([api.get(`/discounts`), api.get(`/admin/users`)]);
    setItems(d.data || []);
    setUsers(u.data || []);
  };

  useEffect(() => { fetchAll(); }, []);

  const resetForm = () => { setForm(EMPTY_FORM); setEditingId(null); };

  const startEdit = (it) => {
    setForm({ category: it.category || "", name: it.name || "", discount: it.discount || "", link: it.link || "", notes: it.notes || "" });
    setEditingId(it.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitDiscount = async () => {
    try {
      if (editingId) {
        const res = await api.put(`/discounts/${editingId}`, form);
        setItems(prev => prev.map(x => x.id === editingId ? res.data : x));
        toast.success("Discount updated");
      } else {
        const res = await api.post(`/discounts`, form);
        setItems(prev => [res.data, ...prev]);
        toast.success("Discount added");
      }
      resetForm();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  const delItem = async (id) => {
    await api.delete(`/discounts/${id}`);
    setItems(prev => prev.filter(x => x.id !== id));
    if (editingId === id) resetForm();
    toast.success("Discount deleted");
  };

  const createUser = async () => {
    try {
      await api.post(`/admin/users`, newUser);
      setNewUser({ email: "", name: "", password: "", is_admin: false });
      await fetchAll();
      toast.success("User created");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  };

  const deleteUser = async (id) => {
    await api.delete(`/admin/users/${id}`);
    await fetchAll();
    toast.success("User deleted");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-xl font-bold tracking-tight bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent">
              Member Discounts
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30 font-medium">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">← Dashboard</a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        <h1 className="text-2xl font-semibold" data-testid="admin-heading">Admin Panel</h1>

        {/* Discount form */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle data-testid="create-discount-title" className="flex items-center gap-2">
              {editingId ? "Edit Discount" : "Add Discount"}
              {editingId && (
                <button onClick={resetForm} className="text-xs text-muted-foreground hover:text-foreground ml-2 font-normal underline">
                  cancel
                </button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Input data-testid="discount-category-input" placeholder="e.g. Travel" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input data-testid="discount-name-input" placeholder="e.g. Hotel Chain" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Discount</Label>
                <Input data-testid="discount-discount-input" placeholder="e.g. 20% off" value={form.discount} onChange={e => setForm({ ...form, discount: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Link</Label>
                <Input data-testid="discount-link-input" placeholder="https://…" value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} />
              </div>
              <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
                <Label>Notes</Label>
                <Input data-testid="discount-notes-input" placeholder="Any additional details" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button data-testid="discount-create-button" onClick={submitDiscount}>
                {editingId ? "Save Changes" : "Add Discount"}
              </Button>
              {editingId && <Button variant="outline" onClick={resetForm}>Cancel</Button>}
            </div>
          </CardContent>
        </Card>

        {/* Discounts table */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle data-testid="discounts-list-title">
              Manage Discounts
              <span className="ml-2 text-sm font-normal text-muted-foreground">({items.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/60 hover:bg-transparent">
                    <TableHead>Category</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Discount</TableHead>
                    <TableHead>Link</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.id} className={`border-border/40 transition-colors ${editingId === it.id ? "bg-primary/5" : "hover:bg-muted/30"}`}>
                      <TableCell className="text-sm">{it.category || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="font-medium">{it.name || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="text-emerald-400 font-semibold">{it.discount || <span className="text-muted-foreground font-normal">—</span>}</TableCell>
                      <TableCell>{it.link ? <a className="text-primary hover:underline text-sm" href={it.link} target="_blank" rel="noreferrer">Open →</a> : <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-normal">{it.notes || "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => startEdit(it)}
                            className="text-xs"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            data-testid={`discount-delete-${it.id}`}
                            onClick={() => delItem(it.id)}
                            className="text-xs"
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-muted-foreground" data-testid="admin-empty-discounts">
                        No discounts yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Users */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle data-testid="users-manage-title">
              Users
              <span className="ml-2 text-sm font-normal text-muted-foreground">({users.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <p className="text-sm text-muted-foreground mb-3">Add a new user</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Input data-testid="newuser-email-input" placeholder="Email (optional)" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
                <Input data-testid="newuser-name-input" placeholder="Username (optional)" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} />
                <Input data-testid="newuser-password-input" placeholder="Password (optional)" type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} />
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newUser.is_admin}
                      onChange={e => setNewUser({ ...newUser, is_admin: e.target.checked })}
                      className="rounded"
                    />
                    Admin
                  </label>
                  <Button data-testid="newuser-create-button" onClick={createUser} className="flex-1">
                    Create
                  </Button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/60 hover:bg-transparent">
                    <TableHead>Username</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id} className="border-border/40 hover:bg-muted/30">
                      <TableCell className="font-medium">{u.name || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.email || "—"}</TableCell>
                      <TableCell>
                        {u.is_admin
                          ? <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30 font-medium">Admin</span>
                          : <span className="text-xs text-muted-foreground">Member</span>
                        }
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="destructive" size="sm" data-testid={`user-delete-${u.id}`} onClick={() => deleteUser(u.id)} className="text-xs">
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground" data-testid="empty-users">No users</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
